import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { PROCESSING_QUERY_REFETCH_INTERVAL_MS } from 'config/queryPolicy'
import { fetchConversationById, fetchMessagesForConversation, fetchUserConversations } from 'modules/coaching/api'
import { coachingKeys } from 'modules/coaching/query-keys'
import { AgentMessage } from 'modules/coaching/types'
import { enrichAgentContents } from 'modules/coaching/utils/agentParsing'
import { getConversationFiles } from 'modules/coaching/utils/conversationFiles'

import type { components } from '@startech/openapi'

const PAGE_SIZE = 12
type GetConversationResponse = components['schemas']['GetConversationResponse']
type ListConversationsResponse = components['schemas']['ListConversationsResponse']

export const getConversationRefetchInterval = (response: GetConversationResponse | undefined): number | undefined => {
  const files = getConversationFiles(response?.data)

  const hasProcessingFile = files.some(
    file => file.processing_status === 'pending' || file.processing_status === 'processing'
  )
  return hasProcessingFile ? PROCESSING_QUERY_REFETCH_INTERVAL_MS : undefined
}

export const emptyConversationsPage = (): ListConversationsResponse => ({
  data: [],
  pagination: {
    page: 1,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
  },
})

export const resolveConversationsPageParameter = (pageParameter: unknown): number => {
  return typeof pageParameter === 'number' ? pageParameter : 1
}

export const getConversationsNextPageParameter = (lastPage: ListConversationsResponse): number | undefined => {
  if (lastPage.pagination.hasNextPage && typeof lastPage.pagination.page === 'number') {
    return lastPage.pagination.page + 1
  }
  return undefined
}

export const conversationDetailQueryFunction = async (
  conversationId: string | undefined
): Promise<GetConversationResponse | undefined> => {
  if (!conversationId) {
    return undefined
  }

  return fetchConversationById(conversationId)
}

export const userConversationsQueryFunction = (
  userId: string | undefined,
  pageParameter: unknown
): Promise<ListConversationsResponse> => {
  if (!userId) {
    return Promise.resolve(emptyConversationsPage())
  }

  return fetchUserConversations(resolveConversationsPageParameter(pageParameter), PAGE_SIZE)
}

export const conversationMessagesQueryFunction = async (
  conversationId: string | undefined
): Promise<AgentMessage[]> => {
  if (!conversationId) {
    return []
  }

  const messages = await fetchMessagesForConversation(conversationId)
  return messages.map(message => ({
    ...message,
    contents: message.role === 'agent' ? enrichAgentContents(message.contents) : message.contents,
  }))
}

export const useConversationQuery = (conversationId: string | undefined, viewerKey = 'anonymous', enabled = true) => {
  return useQuery<GetConversationResponse | undefined>({
    queryKey: coachingKeys.conversationDetail(conversationId ?? null, viewerKey),
    queryFn: () => conversationDetailQueryFunction(conversationId),
    enabled: Boolean(conversationId) && enabled,
    refetchInterval: query => getConversationRefetchInterval(query.state.data),
  })
}

export const useUserConversationsInfiniteQuery = (userId: string | undefined) => {
  return useInfiniteQuery<ListConversationsResponse>({
    queryKey: coachingKeys.conversationList(userId ?? null),
    queryFn: ({ pageParam }) => userConversationsQueryFunction(userId, pageParam),
    initialPageParam: 1,
    getNextPageParam: getConversationsNextPageParameter,
    enabled: Boolean(userId),
  })
}

export const useConversationMessagesQuery = (
  conversationId: string | undefined,
  viewerKey = 'anonymous',
  enabled = true
) => {
  return useQuery<AgentMessage[]>({
    queryKey: coachingKeys.conversationMessages(conversationId ?? null, viewerKey),
    queryFn: () => conversationMessagesQueryFunction(conversationId),
    enabled: Boolean(conversationId) && enabled,
  })
}
